// Canvas-2D rendering for the simulator. World-space transform with a
// follow camera and PIXELS_PER_METER scaling — passing meters straight
// to drawImage / strokeRect / etc.
//
// `render(ctx, world)` is the single entry point. It handles the
// camera lerp, applies the world transform, draws all layers, and
// restores. Caller is responsible for canvas sizing + the rAF loop.
//
// Layer order is load-bearing — the prototype's drawTrack comments
// explain why each layer goes where. Don't reorder without testing
// against the editor's authored geometry; the joins between pit lane,
// merge ribbons, and the racing surface depend on this exact stacking.

import { SURFACE_PROPS, CURB_COLORS, TARMAC_COLORS, patchLayer } from '../../shared/surfaces.js';
import { markerLineEndpoints, SECTOR_COLORS } from '../../shared/markers.js';
import { computeGridSlots } from './grid.js';

export const PIXELS_PER_METER = 4.5;

const _bgImageCache = new Map();
function getCachedBgImage(dataUrl, onLoad) {
  if (!dataUrl) return null;
  let img = _bgImageCache.get(dataUrl);
  if (img) return img;
  img = new Image();
  if (onLoad) img.addEventListener('load', onLoad, { once: true });
  img.src = dataUrl;
  _bgImageCache.set(dataUrl, img);
  return img;
}

// Pre-warm the bg image cache and resolve when the image is decoded.
// Used by mountSimulator's `ready` promise so hosts can wait on
// static-layer assets before revealing the canvas. Resolves
// immediately if no dataUrl, or on a load/error event otherwise.
// Returns the cached Image so the render path picks up the same
// instance rather than fetching twice.
export function preloadBgImage(dataUrl) {
  if (!dataUrl) return Promise.resolve(null);
  const img = getCachedBgImage(dataUrl);
  if (img.complete && img.naturalWidth > 0) return Promise.resolve(img);
  return new Promise((resolve) => {
    const finish = () => resolve(img);
    img.addEventListener('load',  finish, { once: true });
    img.addEventListener('error', finish, { once: true });
  });
}

// Static-layer cache geometry: the cache canvas covers a 2× viewport
// in each dimension (centered on the camera at last build time), and
// the renderer triggers a rebuild when the camera has moved more than
// CACHE_REBUILD_TRIGGER × viewport from the cache center. The 0.4
// trigger leaves ~0.1× viewport of margin past the visible area at
// rebuild time — far enough that the user never sees the cache edge,
// while keeping rebuild frequency reasonable (~once per second of
// driving on a typical track).
const CACHE_VIEWPORT_MULT     = 2;
const CACHE_REBUILD_TRIGGER   = 0.4;

export function render(ctx, world, { onBgImageLoad } = {}) {
  const { track, cars, camera, selectedCarIdx } = world;
  const canvas = ctx.canvas;
  const w = canvas.width, h = canvas.height;

  // Camera follows the selected car. Velocity look-ahead biases the
  // viewport toward where the car is heading so the player has time
  // to react. Same lerp/look-ahead constants as the single-car path.
  const focus = cars[selectedCarIdx] || cars[0];
  if (focus) {
    const targetX = focus.pos.x + focus.vel.x * 0.4;
    const targetY = focus.pos.y + focus.vel.y * 0.4;
    camera.x += (targetX - camera.x) * 0.08;
    camera.y += (targetY - camera.y) * 0.08;
  }

  // Background — the default surface color, fills the viewport. Acts
  // as a fallback fill outside the static cache's covered area.
  const bgSurface = track.defaultSurface || 'grass';
  ctx.fillStyle = SURFACE_PROPS[bgSurface]?.color || '#1a2e1a';
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.scale(PIXELS_PER_METER, PIXELS_PER_METER);
  ctx.translate(-camera.x, -camera.y);

  // Static layers via the cache: rebuild on demand, then blit at its
  // recorded world position. Saves rebuilding ~50 stroke/fill paths
  // per frame and (more importantly on low-end devices) avoids
  // repeated software rasterization of the same shapes.
  const cache = ensureStaticCache(world, ctx, onBgImageLoad);
  if (cache) {
    ctx.drawImage(
      cache.canvas,
      cache.centerX - cache.widthM  / 2,
      cache.centerY - cache.heightM / 2,
      cache.widthM,
      cache.heightM,
    );
  }

  // Draw all cars. Render the selected car last so its highlight ring
  // sits on top of overlapping cars. Recovered cars (post-FCC cleanup)
  // are hidden — they've been towed off and no longer exist on track.
  for (let i = 0; i < cars.length; i++) {
    if (i === selectedCarIdx) continue;
    if (cars[i]._recovered) continue;
    drawCar(ctx, cars[i], false);
  }
  if (focus && !focus._recovered) drawCar(ctx, focus, true);

  ctx.restore();
}

// Ensure world._renderState.cache is up to date with the current
// camera position, viewport size, and car count. Re-renders the
// static layers (background image + track + walls + patches + grid
// slots) into an offscreen canvas when:
//   - first call (no cache yet)
//   - viewport size changed (window resize)
//   - car count changed (grid slot painting differs)
//   - camera moved past the rebuild-trigger band
// The cache is kept transparent outside the drawn track features so
// the main canvas's default-surface fill shows through anywhere the
// cache hasn't covered.
function ensureStaticCache(world, mainCtx, onBgImageLoad) {
  const canvas = mainCtx.canvas;
  if (canvas.width === 0 || canvas.height === 0) return null;

  const renderState = world._renderState ?? (world._renderState = { cache: null });
  const cache       = renderState.cache;

  const cacheWPx = canvas.width  * CACHE_VIEWPORT_MULT;
  const cacheHPx = canvas.height * CACHE_VIEWPORT_MULT;
  const cacheWM  = cacheWPx / PIXELS_PER_METER;
  const cacheHM  = cacheHPx / PIXELS_PER_METER;

  let needsRebuild = false;
  if (!cache) {
    needsRebuild = true;
  } else if (
    cache.canvas.width  !== cacheWPx ||
    cache.canvas.height !== cacheHPx
  ) {
    needsRebuild = true;
  } else if (cache.carCount !== world.cars.length) {
    needsRebuild = true;
  } else {
    const triggerMx = (canvas.width  / PIXELS_PER_METER) * CACHE_REBUILD_TRIGGER;
    const triggerMy = (canvas.height / PIXELS_PER_METER) * CACHE_REBUILD_TRIGGER;
    if (Math.abs(world.camera.x - cache.centerX) > triggerMx ||
        Math.abs(world.camera.y - cache.centerY) > triggerMy) {
      needsRebuild = true;
    }
  }

  if (!needsRebuild) return cache;

  // Reuse the existing offscreen canvas across rebuilds so we don't
  // thrash the graphics-resource allocator.
  let offCanvas = cache?.canvas;
  if (!offCanvas) offCanvas = document.createElement('canvas');
  if (offCanvas.width !== cacheWPx)  offCanvas.width  = cacheWPx;
  if (offCanvas.height !== cacheHPx) offCanvas.height = cacheHPx;
  const off = offCanvas.getContext('2d');
  off.setTransform(1, 0, 0, 1, 0, 0);
  off.clearRect(0, 0, cacheWPx, cacheHPx);
  off.translate(cacheWPx / 2, cacheHPx / 2);
  off.scale(PIXELS_PER_METER, PIXELS_PER_METER);
  off.translate(-world.camera.x, -world.camera.y);

  // Background image's onLoad callback re-invalidates so the next
  // frame's cache rebuild includes the now-loaded image.
  drawBackgroundImage(off, world.track, () => invalidateRenderCache(world, onBgImageLoad));
  drawTrack(off, world.track, world.geometry, world.cars);
  drawGridSlots(off, world.track, world.geometry, world.cars.length);

  renderState.cache = {
    canvas:   offCanvas,
    centerX:  world.camera.x,
    centerY:  world.camera.y,
    widthM:   cacheWM,
    heightM:  cacheHM,
    carCount: world.cars.length,
  };
  return renderState.cache;
}

// Force a full rebuild on the next render. Callers: window resize,
// background-image load, anything that changes the static geometry
// the cache captured.
export function invalidateRenderCache(world, alsoNotify) {
  if (world && world._renderState) world._renderState.cache = null;
  if (alsoNotify) alsoNotify();
}

function drawBackgroundImage(ctx, track, onLoad) {
  const bg = track.backgroundImage;
  if (!bg) return;
  const opacity = bg.opacity ?? 0.6;
  if (opacity <= 0) return;
  const img = getCachedBgImage(bg.dataUrl, onLoad);
  if (!img || !img.complete || img.naturalWidth === 0) return;

  const mpp = bg.metersPerPixel || 0.5;
  const widthM  = img.naturalWidth  * mpp;
  const heightM = img.naturalHeight * mpp;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.translate(bg.centerX || 0, bg.centerY || 0);
  ctx.rotate(bg.rotation || 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, -widthM / 2, -heightM / 2, widthM, heightM);
  ctx.restore();
}

function drawTrack(ctx, track, geometry, cars) {
  const cl = geometry.centerline;
  if (cl.length < 2) return;
  const direction = track.direction ?? 'cw';

  // Layer 1: BELOW-strip patches (grass / gravel / sand). Off-track
  // surfaces — they sit beneath everything else.
  drawPatches(ctx, geometry, 'below');

  // Layer 2: MERGE patches (tarmac). Drawn before curbs and the
  // strip so both paint over tarmac patches in the overlap. Past the
  // strip's edge, tarmac patches stay visible (drawn after `below`).
  drawPatches(ctx, geometry, 'merge');

  // Layer 3: ABOVE patches (curbs). Painted on top of tarmac
  // patches at corner apexes, but BELOW the racing strip + pit lane
  // — curbs that bleed into the strip get hidden by the strip fill,
  // matching how a real curb sits at the track edge rather than on
  // the asphalt.
  drawPatches(ctx, geometry, 'above');

  // Layer 4: pit lane merge ribbons + pit lane band. Both are
  // tarmac-colored "racing surfaces" with white edge lines.
  drawPitLaneRibbons(ctx, geometry);
  drawPitLane(ctx, geometry);
  drawPitBoxes(ctx, geometry);
  drawPitBoxLabels(ctx, geometry, cars);

  // Layer 5: main track surface — variable-width filled polygon. The
  // white edge band sits 0.3 m beyond the tarmac on each side. Both
  // edge sets are pre-built at track-load (geometry.centerlineEdges /
  // geometry.bandEdges).
  const tarmac = geometry.centerlineEdges;
  const band   = geometry.bandEdges;
  if (tarmac && band && tarmac.outer.length >= 3) {
    fillAnnulus(ctx, band.outer, band.inner, '#cfcfcf');
    fillAnnulus(ctx, tarmac.outer, tarmac.inner, SURFACE_PROPS.tarmac.color);
  }

  // Layer 6: start/finish lines (main + pit) + the optional start
  // line. Drawn before walls so a wall that crosses the line
  // obscures it.
  drawStartFinishLine(ctx, track, direction);
  drawStartLine(ctx, track, direction);
  drawPitStartFinishLine(ctx, track);

  // Layer 7: walls — top of everything.
  drawWalls(ctx, geometry);
}

// Iterate the geometry-cached patches in priority-ascending order
// (lower priority drawn first → higher priority paints on top). The
// patches array is already sorted by comparePatchPriority at
// track-load. Each entry carries .dense + render-style fields, so
// drawOnePatch never touches the schema or re-densifies.
function drawPatches(ctx, geometry, layer) {
  const patches = geometry.patches;
  for (let i = 0; i < patches.length; i++) {
    if (patchLayer(patches[i]) !== layer) continue;
    drawOnePatch(ctx, patches[i]);
  }
}

// Fill a closed-loop annulus from two equal-length closed polylines.
// Each is emitted as its own closed sub-path; evenodd fill then
// covers the area between them regardless of winding direction. Use
// for the track surface (and the white edge band). Open-ribbon fills
// (pit lane) go through fillRibbon below.
function fillAnnulus(ctx, outerList, innerList, fillStyle) {
  if (outerList.length < 3 || innerList.length !== outerList.length) return;
  ctx.fillStyle = fillStyle;
  ctx.beginPath();
  ctx.moveTo(outerList[0].x, outerList[0].y);
  for (let i = 1; i < outerList.length; i++) ctx.lineTo(outerList[i].x, outerList[i].y);
  ctx.closePath();
  ctx.moveTo(innerList[0].x, innerList[0].y);
  for (let i = 1; i < innerList.length; i++) ctx.lineTo(innerList[i].x, innerList[i].y);
  ctx.closePath();
  ctx.fill('evenodd');
}

// Fill an open ribbon polygon: outer forward, inner backward, closed.
// Use for the pit lane band — the ribbon has clean endpoints, so a
// single closed polygon is correct.
function fillRibbon(ctx, outerList, innerList, fillStyle) {
  if (outerList.length === 0 || innerList.length !== outerList.length) return;
  ctx.fillStyle = fillStyle;
  ctx.beginPath();
  ctx.moveTo(outerList[0].x, outerList[0].y);
  for (let i = 1; i < outerList.length; i++) ctx.lineTo(outerList[i].x, outerList[i].y);
  for (let i = innerList.length - 1; i >= 0; i--) ctx.lineTo(innerList[i].x, innerList[i].y);
  ctx.closePath();
  ctx.fill();
}

function drawStartFinishLine(ctx, track, direction) {
  if (!track.startFinish) return;
  const ends = markerLineEndpoints(track.startFinish, track.trackline, direction);
  if (!ends) return;
  drawCheckeredFinishLine(ctx, ends.a, ends.b, 1.0);
}

// Plain white line spanning the track at the optional start-line
// marker (different from S/F on tracks where the grid is offset).
// Lap 1 timing begins when the leader crosses this line. The
// checker pattern is reserved for S/F so the two lines read as
// distinct race-control elements.
function drawStartLine(ctx, track, direction) {
  if (!track.startLine) return;
  const ends = markerLineEndpoints(track.startLine, track.trackline, direction);
  if (!ends) return;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(ends.a.x, ends.a.y);
  ctx.lineTo(ends.b.x, ends.b.y);
  ctx.strokeStyle = '#f5f5f5';
  ctx.lineWidth = 0.4;       // ~40 cm thick — matches typical paint stripe
  ctx.lineCap = 'butt';
  ctx.stroke();
  ctx.restore();
}

// Grid-box paint per starting slot. F1-style rectangle: a forward
// line at the slot's nose position, plus two short side lines
// pointing forward, forming a "U" facing the direction of travel.
// Same computeGridSlots helper that spawnCarsOnGrid uses, so painted
// boxes always match where cars actually spawn.
function drawGridSlots(ctx, track, geometry, slotCount) {
  if (!slotCount) return;
  const slots = computeGridSlots(track, geometry, slotCount);
  if (!slots.length) return;

  ctx.save();
  ctx.strokeStyle = '#f5f5f5';
  ctx.lineWidth = 0.18;          // ~18 cm paint stripe
  ctx.lineCap = 'butt';

  // Box dimensions in metres. Width is car width + small margin so
  // the box clearly bounds the car. Length is intentionally short:
  // front line just past the nose, side ticks ~1 m back. Reads as
  // a "parking bracket" rather than a full box around the car.
  const halfWidth = 1.4;
  const halfLength = 2.5;        // front line just past car nose (= 2.3 m)

  for (const slot of slots) {
    const fx = Math.cos(slot.heading);
    const fy = Math.sin(slot.heading);
    const px = -Math.sin(slot.heading);
    const py =  Math.cos(slot.heading);

    // Front line (perpendicular, where the nose stops).
    const frontX = slot.x + fx * halfLength;
    const frontY = slot.y + fy * halfLength;
    const frontLeftX  = frontX + px * halfWidth, frontLeftY  = frontY + py * halfWidth;
    const frontRightX = frontX - px * halfWidth, frontRightY = frontY - py * halfWidth;

    // Side lines extend backward from the front corners as short
    // tick marks. Open at the back gives a "U" that reads as a
    // grid box without painting all four sides — real F1 grid
    // boxes do exactly this. Kept short because the full-length
    // box covered most of the car and looked too dense.
    const sideBackLen = 1.0;
    const backLeftX  = frontLeftX  - fx * sideBackLen, backLeftY  = frontLeftY  - fy * sideBackLen;
    const backRightX = frontRightX - fx * sideBackLen, backRightY = frontRightY - fy * sideBackLen;

    ctx.beginPath();
    // Front (nose) line.
    ctx.moveTo(frontLeftX,  frontLeftY);
    ctx.lineTo(frontRightX, frontRightY);
    // Left side line.
    ctx.moveTo(frontLeftX,  frontLeftY);
    ctx.lineTo(backLeftX,   backLeftY);
    // Right side line.
    ctx.moveTo(frontRightX, frontRightY);
    ctx.lineTo(backRightX,  backRightY);
    ctx.stroke();
  }
  ctx.restore();
}

// Pit-lane S/F (when authored). Solid white stripe — the checker
// pattern is reserved for the main track's S/F so the two lines read
// as distinct race-control elements at a glance.
function drawPitStartFinishLine(ctx, track) {
  const pl = track.pitLane;
  if (!pl || !pl.startFinish) return;
  const ends = markerLineEndpoints(pl.startFinish, pl, 'cw', false);
  if (!ends) return;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(ends.a.x, ends.a.y);
  ctx.lineTo(ends.b.x, ends.b.y);
  ctx.strokeStyle = '#f5f5f5';
  ctx.lineWidth = 0.4;       // ~40 cm thick — matches typical paint stripe
  ctx.lineCap = 'butt';
  ctx.stroke();
  ctx.restore();
}

// Two offset rows of alternating black/white chunks along the line
// from a→b — the classic motorsport checker. Each cell is roughly
// square (cell side ≈ totalThickness / 2). Worked in world units;
// the canvas transform handles the screen mapping.
function drawCheckeredFinishLine(ctx, a, b, totalThickness) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenWorld = Math.hypot(dx, dy);
  if (lenWorld < 0.1) return;

  const rowThickness = totalThickness / 2;
  const cellLen = rowThickness;     // square cells
  const numChunks = Math.max(6, Math.round(lenWorld / cellLen));

  // Perpendicular unit vector for the row offsets.
  const px = -dy / lenWorld;
  const py =  dx / lenWorld;

  ctx.lineWidth = rowThickness;
  ctx.lineCap = 'butt';

  for (let row = 0; row < 2; row++) {
    // Row centers at ±rowThickness/2 from the line a→b so the band
    // is symmetric and totalThickness deep overall.
    const sign = row === 0 ? -1 : 1;
    const ox = px * sign * rowThickness / 2;
    const oy = py * sign * rowThickness / 2;

    for (let i = 0; i < numChunks; i++) {
      // (i + row) % 2 makes the second row's checker pattern offset
      // by one cell from the first.
      const isWhite = (i + row) % 2 === 0;
      const t1 = i / numChunks;
      const t2 = (i + 1) / numChunks;
      ctx.strokeStyle = isWhite ? '#fff' : '#0c0f12';
      ctx.beginPath();
      ctx.moveTo(a.x + dx * t1 + ox, a.y + dy * t1 + oy);
      ctx.lineTo(a.x + dx * t2 + ox, a.y + dy * t2 + oy);
      ctx.stroke();
    }
  }
}

function drawOnePatch(ctx, patch) {
  const dense = patch.dense;
  if (!dense || dense.length < 2) return;
  // Tarmac patches can carry a `tarmacColor` key (TARMAC_COLORS) for
  // visually distinct paved zones (apron, alt line) — physics is
  // unchanged. Other surfaces use SURFACE_PROPS.<surface>.color.
  const fillColor = patch.surface === 'tarmac'
    ? (TARMAC_COLORS[patch.tarmacColor] || SURFACE_PROPS.tarmac.color)
    : (SURFACE_PROPS[patch.surface]?.color || SURFACE_PROPS.grass.color);

  if (patch.closed) {
    if (dense.length < 3) return;
    ctx.beginPath();
    ctx.moveTo(dense[0].x, dense[0].y);
    for (let i = 1; i < dense.length; i++) ctx.lineTo(dense[i].x, dense[i].y);
    ctx.closePath();
    ctx.fillStyle = fillColor;
    ctx.fill();
  } else {
    const thickness = patch.thickness ?? 8.0;

    if (patch.surface === 'curb') {
      drawCurbStripes(ctx, dense, thickness, patch.curbColor || 'red');
      return;
    }

    // End-cap is author-controlled for non-curb open bands. Unset =
    // 'round' (the historical default). 'butt' ends flush with the
    // last waypoint; 'square' extends a half-thickness past it.
    const endCap = patch.endCap || 'round';
    ctx.beginPath();
    ctx.moveTo(dense[0].x, dense[0].y);
    for (let i = 1; i < dense.length; i++) ctx.lineTo(dense[i].x, dense[i].y);
    ctx.strokeStyle = fillColor;
    ctx.lineWidth = thickness;
    ctx.lineCap = endCap;
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
}

// Render an open-band curb. Red curbs alternate red/white chunks
// (Monaco / classic F1) — chunk length scales with thickness so each
// block is roughly square (~50 cm × 50 cm), floored at 0.4 m so thin
// curbs still get visible stripes. Square caps + miter joins keep
// band ends and chunk boundaries crisp. Other colors (yellow/blue/grey)
// render as a solid band — authors wanted accent curbs without the
// candy-stripe pattern.
function drawCurbStripes(ctx, dense, thickness, colorKey = 'red') {
  const colorStripe = CURB_COLORS[colorKey] || CURB_COLORS.red;

  ctx.lineWidth   = thickness;
  ctx.lineCap     = 'butt';
  ctx.lineJoin    = 'miter';
  ctx.miterLimit  = 4;

  if (colorKey !== 'red') {
    ctx.strokeStyle = colorStripe;
    ctx.beginPath();
    ctx.moveTo(dense[0].x, dense[0].y);
    for (let i = 1; i < dense.length; i++) ctx.lineTo(dense[i].x, dense[i].y);
    ctx.stroke();
    return;
  }

  const chunkLenM = Math.max(0.4, thickness);
  const colorWhite = '#f5f5f5';

  let chunkStart = 0;
  let accumulated = 0;
  let chunkIdx = 0;

  const strokeChunk = (from, to, idx) => {
    if (to <= from) return;
    ctx.strokeStyle = idx % 2 === 0 ? colorWhite : colorStripe;
    ctx.beginPath();
    ctx.moveTo(dense[from].x, dense[from].y);
    for (let j = from + 1; j <= to; j++) ctx.lineTo(dense[j].x, dense[j].y);
    ctx.stroke();
  };

  for (let i = 1; i < dense.length; i++) {
    accumulated += Math.hypot(dense[i].x - dense[i - 1].x, dense[i].y - dense[i - 1].y);
    const isLast = i === dense.length - 1;
    if (accumulated >= chunkLenM || isLast) {
      strokeChunk(chunkStart, i, chunkIdx);
      chunkStart = i;
      accumulated = 0;
      chunkIdx++;
    }
  }
}

function drawPitLaneRibbons(ctx, geometry) {
  if (!geometry.ribbons || geometry.ribbons.length === 0) return;
  const tarmacColor = SURFACE_PROPS.tarmac.color;
  for (const ribbon of geometry.ribbons) {
    const dense = ribbon.dense;
    if (dense.length < 2) continue;
    const widthWorld = ribbon.halfWidth * 2;

    ctx.beginPath();
    ctx.moveTo(dense[0].x, dense[0].y);
    for (let i = 1; i < dense.length; i++) ctx.lineTo(dense[i].x, dense[i].y);
    ctx.strokeStyle = tarmacColor;
    ctx.lineWidth = widthWorld;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'round';
    ctx.stroke();

    legacyDrawSplineEdgeLines(ctx, dense, ribbon.halfWidth);
  }
}

// Pit boxes — auto-generated tarmac quads on the pit-lane edge,
// matching the visual style of pit boxes in real racing (square tarmac
// + white separator lines between adjacent boxes). Drawn after the pit
// lane so the boxes' inner edge sits flush against the lane.
function drawPitBoxes(ctx, geometry) {
  const pb = geometry.pitBoxes;
  if (!pb || !pb.boxes.length) return;
  // Tarmac fill — polygon = innerEdge forward + outerEdge reversed.
  // No outer rail stroke (per user choice — boxes look like ticks +
  // tarmac, not enclosed rectangles).
  ctx.fillStyle = SURFACE_PROPS.tarmac.color;
  for (const box of pb.boxes) {
    ctx.beginPath();
    const inner = box.innerEdge;
    const outer = box.outerEdge;
    ctx.moveTo(inner[0].x, inner[0].y);
    for (let i = 1; i < inner.length; i++) ctx.lineTo(inner[i].x, inner[i].y);
    for (let i = outer.length - 1; i >= 0; i--) ctx.lineTo(outer[i].x, outer[i].y);
    ctx.closePath();
    ctx.fill();
  }
  // Perpendicular dividers between team boxes (back edge of each plus
  // the front edge of the final box).
  ctx.strokeStyle = '#cfcfcf';
  ctx.lineWidth = 0.25;
  ctx.lineCap = 'butt';
  ctx.beginPath();
  for (let bi = 0; bi < pb.boxes.length; bi++) {
    const box = pb.boxes[bi];
    ctx.moveTo(box.innerEdge[0].x, box.innerEdge[0].y);
    ctx.lineTo(box.outerEdge[0].x, box.outerEdge[0].y);
    if (bi === pb.boxes.length - 1) {
      const li = box.innerEdge.length - 1;
      const lo = box.outerEdge.length - 1;
      ctx.moveTo(box.innerEdge[li].x, box.innerEdge[li].y);
      ctx.lineTo(box.outerEdge[lo].x, box.outerEdge[lo].y);
    }
  }
  ctx.stroke();

  // Back-side rail — white line along the outer edge running the full
  // row. Skips the shared boundary point between adjacent boxes.
  ctx.lineWidth = 0.15;
  ctx.beginPath();
  for (let bi = 0; bi < pb.boxes.length; bi++) {
    const outer = pb.boxes[bi].outerEdge;
    const startI = bi === 0 ? 0 : 1;
    for (let i = startI; i < outer.length; i++) {
      if (bi === 0 && i === 0) ctx.moveTo(outer[i].x, outer[i].y);
      else                     ctx.lineTo(outer[i].x, outer[i].y);
    }
  }
  ctx.stroke();
}

// Driver-number labels on assigned pit boxes. Pulls each car's
// `carNumber` (set from the entries roster at world creation) and
// paints it at the visual center of that car's box. Boxes without
// an assigned car (e.g. extra slots when grid < maxCount) stay blank.
// Text aligns with the lane tangent through the box (along the box's
// long axis) so numbers read naturally as the camera follows a car
// down the lane.
function drawPitBoxLabels(ctx, geometry, cars) {
  const pb = geometry.pitBoxes;
  if (!pb || !pb.boxes.length || !cars) return;
  const labels = new Map();
  for (const car of cars) {
    if (car.pitBoxIdx != null && typeof car.carNumber === 'number') {
      labels.set(car.pitBoxIdx, car.carNumber);
    }
  }
  if (labels.size === 0) return;

  ctx.save();
  ctx.fillStyle = '#f5c441';       // yellow — matches the pit speed-zone marker palette
  ctx.font = '2.4px sans-serif';   // world meters — readable at typical zoom, fits the 8m × 4m box
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const [idx, num] of labels) {
    const box = pb.boxes[idx];
    if (!box) continue;
    // 4-corner centroid for the visual center. The mid-vertex of
    // innerEdge isn't the geometric center on curved lanes (boxes
    // get extra interior vertices where the lane bends), so labels
    // drift toward the curve. Averaging the four box corners is
    // robust to vertex count and reads as the box's true center.
    const inner = box.innerEdge;
    const outer = box.outerEdge;
    const iLast = inner.length - 1;
    const oLast = outer.length - 1;
    const cx = (inner[0].x + inner[iLast].x + outer[0].x + outer[oLast].x) * 0.25;
    const cy = (inner[0].y + inner[iLast].y + outer[0].y + outer[oLast].y) * 0.25;
    // Text orientation: chord direction of the inner edge — the box's
    // long axis as a single straight line. Approximation rather than
    // local tangent, but consistent across all boxes and cheap.
    const dx = inner[iLast].x - inner[0].x;
    const dy = inner[iLast].y - inner[0].y;
    const angle = Math.atan2(dy, dx);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.fillText(String(num), 0, 0);
    ctx.restore();
  }
  ctx.restore();
}

function drawPitLane(ctx, geometry) {
  const pl = geometry.pitLane;
  if (!pl || pl.dense.length < 2) return;

  // Variable-width tarmac fill — open ribbon polygon. Falls back to
  // the constant-width edge build (perp from chord) if a track loaded
  // before track-geometry was extended is in play.
  if (pl.inner && pl.outer && pl.inner.length === pl.outer.length) {
    fillRibbon(ctx, pl.outer, pl.inner, SURFACE_PROPS.tarmac.color);
    drawSplineEdgeLines(ctx, pl.inner, pl.outer);
  } else {
    // Fallback path: legacy constant halfWidth.
    const widthWorld = pl.halfWidth * 2;
    ctx.beginPath();
    ctx.moveTo(pl.dense[0].x, pl.dense[0].y);
    for (let i = 1; i < pl.dense.length; i++) ctx.lineTo(pl.dense[i].x, pl.dense[i].y);
    ctx.strokeStyle = SURFACE_PROPS.tarmac.color;
    ctx.lineWidth = widthWorld;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'round';
    ctx.stroke();
    legacyDrawSplineEdgeLines(ctx, pl.dense, pl.halfWidth);
  }
}

// White edge lines along prebuilt inner / outer rails. The rails are
// expected to already incorporate per-waypoint width overrides, so
// this is just a stroke-each-array operation.
function drawSplineEdgeLines(ctx, inner, outer) {
  ctx.strokeStyle = '#cfcfcf';
  ctx.lineWidth = 0.3;
  ctx.lineCap = 'butt';
  for (const list of [inner, outer]) {
    if (list.length < 2) continue;
    ctx.beginPath();
    ctx.moveTo(list[0].x, list[0].y);
    for (let i = 1; i < list.length; i++) ctx.lineTo(list[i].x, list[i].y);
    ctx.stroke();
  }
}

// Legacy variant: builds inner / outer from a dense polyline + a
// constant halfWidth on the fly. Used by the merge-ribbon renderer
// where the ribbon is short enough that constant width reads cleanly.
function legacyDrawSplineEdgeLines(ctx, dense, halfWidth) {
  const inner = [], outer = [];
  for (let i = 0; i < dense.length - 1; i++) {
    const a = dense[i], b = dense[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    const nx = -dy / len, ny = dx / len;
    inner.push({ x: a.x - nx * halfWidth, y: a.y - ny * halfWidth });
    outer.push({ x: a.x + nx * halfWidth, y: a.y + ny * halfWidth });
  }
  if (dense.length >= 2) {
    const a = dense[dense.length - 2], b = dense[dense.length - 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len > 1e-9) {
      const nx = -dy / len, ny = dx / len;
      inner.push({ x: b.x - nx * halfWidth, y: b.y - ny * halfWidth });
      outer.push({ x: b.x + nx * halfWidth, y: b.y + ny * halfWidth });
    }
  }
  drawSplineEdgeLines(ctx, inner, outer);
}

function drawWalls(ctx, geometry) {
  for (let i = 0; i < geometry.walls.length; i++) drawOneWall(ctx, geometry.walls[i]);
}

function drawOneWall(ctx, wall) {
  const dense = wall.dense;
  if (!dense || dense.length < 2) return;
  const thickness = Math.max(0.3, wall.thickness ?? 0.5);

  ctx.strokeStyle = SURFACE_PROPS.wall.color;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(dense[0].x, dense[0].y);
  for (let i = 1; i < dense.length; i++) ctx.lineTo(dense[i].x, dense[i].y);
  if (wall.closed) ctx.closePath();
  ctx.stroke();
}

// Fallback livery for cars that didn't get one set (shouldn't
// happen in normal createWorld flow, but keeps drawCar safe).
const FALLBACK_LIVERY = { body: '#e54b3c', accent: '#bf3a2c' };

function drawCar(ctx, car, isSelected) {
  ctx.save();
  ctx.translate(car.pos.x, car.pos.y);
  ctx.rotate(car.heading);

  const L = car.spec.carLength;
  const W = car.spec.carWidth;
  const livery = car.livery || FALLBACK_LIVERY;

  // Drop shadow
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(-L / 2 + 0.25, -W / 2 + 0.45, L, W);

  // Body
  ctx.fillStyle = livery.body;
  ctx.fillRect(-L / 2, -W / 2, L, W);

  // Front section accent
  ctx.fillStyle = livery.accent;
  ctx.fillRect(0.2, -W / 2, L / 2 - 0.2, W);

  // Cockpit
  ctx.fillStyle = '#101412';
  ctx.fillRect(-0.5, -W / 2 + 0.45, 1.6, W - 0.9);

  // Wheels
  ctx.fillStyle = '#0a0a0a';
  const wL = 0.65, wW = 0.32;
  // Rear (fixed)
  ctx.fillRect(-1.45 - wL / 2, -W / 2 - 0.05, wL, wW);
  ctx.fillRect(-1.45 - wL / 2,  W / 2 - 0.27, wL, wW);
  // Front (rotated by steering)
  for (const sy of [-W / 2 + 0.11, W / 2 - 0.11]) {
    ctx.save();
    ctx.translate(1.35, sy);
    ctx.rotate(car.steer);
    ctx.fillRect(-wL / 2, -wW / 2, wL, wW);
    ctx.restore();
  }

  ctx.restore();

  // Selection ring — drawn in world coords, unrotated, so it stays
  // round at any heading. Translucent so an overlapping car still
  // shows through. Sized to comfortably enclose the body's diagonal.
  if (isSelected) {
    const r = Math.hypot(L, W) / 2 + 0.4;
    ctx.save();
    ctx.beginPath();
    ctx.arc(car.pos.x, car.pos.y, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 0.18;
    ctx.setLineDash([0.5, 0.35]);
    ctx.stroke();
    ctx.restore();
  }
}
